import React, { useState, useRef, useEffect } from 'react';
import { Download, ExternalLink, RefreshCw, AlertCircle, FileText, Table, BarChart3, Maximize2, Minimize2, Settings, ChevronLeft, ChevronRight } from 'lucide-react';

interface TabbedExcelViewerProps {
  document: any;
  onDownload: () => void;
}

const TabbedExcelViewer: React.FC<TabbedExcelViewerProps> = ({ document, onDownload }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerMode, setViewerMode] = useState<'microsoft' | 'google' | 'direct' | 'download' | 'content'>('content');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentSheet, setCurrentSheet] = useState(1);
  const [totalSheets, setTotalSheets] = useState(3); // Par défaut, on suppose 3 feuilles
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
  }, [document?.id, viewerMode]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleIframeError = () => {
    console.error(`Erreur de chargement du document Excel en mode ${viewerMode}`);
    setError(`Impossible de charger le document Excel en mode ${viewerMode}.`);
    setIsLoading(false);
    
    // Essayer le mode suivant
    if (viewerMode === 'microsoft') {
      setViewerMode('google');
    } else if (viewerMode === 'google') {
      setViewerMode('direct');
    }
  };

  if (!document?.file_url) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Document Excel non disponible</h3>
          <p className="text-gray-500">Le fichier Excel n'est pas accessible pour la lecture.</p>
        </div>
      </div>
    );
  }

  const renderViewer = () => {
    switch (viewerMode) {
      case 'microsoft':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Document Excel</h3>
              <p className="text-gray-600 mb-6">
                Le service Microsoft Office Online nécessite un serveur accessible publiquement.
                Pour un serveur local, utilisez les options ci-dessous :
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.open(document.file_url, '_blank')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2 inline" />
                  Ouvrir dans Excel
                </button>
                <button
                  onClick={() => setViewerMode('content')}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2 inline" />
                  Afficher le contenu
                </button>
                <button
                  onClick={() => setViewerMode('download')}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2 inline" />
                  Télécharger
                </button>
              </div>
            </div>
          </div>
        );
      
      case 'google':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <FileText className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Document Excel</h3>
              <p className="text-gray-600 mb-6">
                Le service Google Sheets nécessite un serveur accessible publiquement.
                Pour un serveur local, utilisez les options ci-dessous :
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.open(document.file_url, '_blank')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2 inline" />
                  Ouvrir dans Excel
                </button>
                <button
                  onClick={() => setViewerMode('content')}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2 inline" />
                  Afficher le contenu
                </button>
                <button
                  onClick={() => setViewerMode('download')}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2 inline" />
                  Télécharger
                </button>
              </div>
            </div>
          </div>
        );
      
      case 'direct':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <FileText className="w-16 h-16 text-purple-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Document Excel</h3>
              <p className="text-gray-600 mb-6">
                Pour afficher ce document Excel, vous pouvez :
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.open(document.file_url, '_blank')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2 inline" />
                  Ouvrir dans Excel
                </button>
                <button
                  onClick={() => window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(document.file_url)}`, '_blank')}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2 inline" />
                  Aperçu Microsoft
                </button>
                <button
                  onClick={() => setViewerMode('content')}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2 inline" />
                  Afficher le contenu
                </button>
                <button
                  onClick={onDownload}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2 inline" />
                  Télécharger
                </button>
              </div>
            </div>
          </div>
        );
      
      case 'download':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <Download className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Télécharger le document</h3>
              <p className="text-gray-600 mb-6">
                Téléchargez ce document Excel pour l'ouvrir dans votre application préférée.
              </p>
              <div className="space-y-3">
                <button
                  onClick={onDownload}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2 inline" />
                  Télécharger le fichier
                </button>
                <button
                  onClick={() => setViewerMode('content')}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Table className="w-4 h-4 mr-2 inline" />
                  Voir le contenu
                </button>
              </div>
            </div>
          </div>
        );
      
      case 'content':
        return (
          <div className="w-full h-full p-6 overflow-auto">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-2xl font-bold text-gray-800">{document.name}</h1>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => window.open(document.file_url, '_blank')}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                    >
                      <ExternalLink className="w-4 h-4 mr-1 inline" />
                      Ouvrir dans Excel
                    </button>
                    <button
                      onClick={() => setViewerMode('download')}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                    >
                      <Download className="w-4 h-4 mr-1 inline" />
                      Télécharger
                    </button>
                  </div>
                </div>
                {document.content ? (
                  <div className="prose prose-lg max-w-none">
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {document.content}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Contenu non disponible</h3>
                    <p className="text-gray-500 mb-6">
                      Le contenu de ce document Excel n'a pas pu être extrait automatiquement.
                    </p>
                    <div className="space-y-3">
                      <button
                        onClick={() => window.open(document.file_url, '_blank')}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 mr-2 inline" />
                        Ouvrir dans Excel
                      </button>
                      <button
                        onClick={onDownload}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2 inline" />
                        Télécharger le fichier
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const getViewerModeLabel = (mode: string) => {
    switch (mode) {
      case 'microsoft': return 'Microsoft Office';
      case 'google': return 'Google Sheets';
      case 'direct': return 'Options';
      case 'download': return 'Téléchargement';
      case 'content': return 'Contenu extrait';
      default: return mode;
    }
  };

  return (
    <div className={`w-full h-full flex flex-col bg-white ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center space-x-3">
          <FileText className="w-5 h-5 text-green-500" />
          <span className="font-medium text-gray-700">{document?.name}</span>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            Excel Viewer ({getViewerModeLabel(viewerMode)})
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={onDownload}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Download className="w-4 h-4 mr-2 inline" />
            Télécharger
          </button>
          <button
            onClick={() => window.open(document.file_url, '_blank')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4 mr-2 inline" />
            Nouvel onglet
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white border-b border-gray-200 p-3 flex items-center justify-center space-x-2 shadow-inner">
          <span className="text-sm text-gray-600">Mode d'affichage:</span>
          <button
            onClick={() => setViewerMode('microsoft')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewerMode === 'microsoft' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Microsoft
          </button>
          <button
            onClick={() => setViewerMode('google')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewerMode === 'google' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Google
          </button>
          <button
            onClick={() => setViewerMode('direct')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewerMode === 'direct' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Options
          </button>
          <button
            onClick={() => setViewerMode('content')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewerMode === 'content' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Contenu
          </button>
          <button
            onClick={() => setViewerMode('download')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewerMode === 'download' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Télécharger
          </button>
        </div>
      )}

      {/* Sheet Navigation */}
      {viewerMode === 'content' && (
        <div className="bg-white border-b border-gray-200 p-3">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => setCurrentSheet(Math.max(1, currentSheet - 1))}
              disabled={currentSheet <= 1}
              className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-1 inline" />
              Précédent
            </button>
            <span className="text-sm text-gray-700 px-3">
              Feuille {currentSheet} sur {totalSheets}
            </span>
            <button
              onClick={() => setCurrentSheet(Math.min(totalSheets, currentSheet + 1))}
              disabled={currentSheet >= totalSheets}
              className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
              <ChevronRight className="w-4 h-4 ml-1 inline" />
            </button>
          </div>
        </div>
      )}

      {/* Contenu du visualiseur */}
      <div className="flex-1 bg-gray-100 relative" style={{ minHeight: '600px', height: isFullscreen ? 'calc(100vh - 120px)' : 'calc(100vh - 240px)' }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-green-500 animate-spin mx-auto mb-2" />
              <p className="text-gray-600">Chargement du document Excel...</p>
              <p className="text-sm text-gray-500 mt-1">Mode: {getViewerModeLabel(viewerMode)}</p>
            </div>
          </div>
        )}
        
        {renderViewer()}
        
        {error && (
          <div className="absolute top-4 right-4 bg-red-100 border border-red-300 rounded-lg p-3 max-w-sm z-20">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="text-sm text-red-700 font-medium">Erreur de chargement</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => setViewerMode('direct')}
                    className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                  >
                    Options
                  </button>
                  <button
                    onClick={() => setViewerMode('content')}
                    className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                  >
                    Contenu
                  </button>
                  <button
                    onClick={() => setViewerMode('download')}
                    className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                  >
                    Télécharger
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TabbedExcelViewer;
